/* eslint-disable react/jsx-pascal-case */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Card,
  CardBody,
  Button,
  Label,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody
} from 'reactstrap';
import { ListGroup, ListGroupItem } from 'reactstrap';
import { Loading } from './LoadingComponent';
import { Control, Form, actions } from 'react-redux-form';
import { CSVLink } from 'react-csv';
import Pagination from './Pagination';
import DOMPurify from '../utils/domPurifyConfig';
import { handleHtmlTagAutoComplete, checkHtmlTagAutoComplete } from '../utils/htmlTagAutoComplete';
import { handleArithmeticInput } from '../utils/arithmeticEval';
import { orderWithPinnedFirst } from '../utils/pinnedSort';
import { usePinEnabled } from '../utils/usePinEnabled';
import { handleImagePaste, selectImageTagOnDoubleClick } from '../utils/imagePasteResize';
import { renderMarkdown } from '../utils/renderMarkdown';
import ImageLightbox from './ImageLightbox';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

// Helper function to extract search keywords (excluding exclusion keywords with !)
function extractSearchKeywords(keywordString) {
  if (!keywordString) return [];

  const keywordAndArray = keywordString.split('|');
  const allKeywords = [];

  for (let k = 0; k < keywordAndArray.length; k++) {
    const keywordArray = keywordAndArray[k].split(/[\s\u3000]+/);
    for (let i = 0; i < keywordArray.length; i++) {
      const kw = keywordArray[i];
      if (kw && kw !== ' ' && kw !== '' && kw.split('')[0] !== '!') {
        // Add keyword (lowercased for multi-char, as-is for single char)
        if (kw.length === 1) {
          allKeywords.push(kw);
        } else {
          allKeywords.push(kw.toLowerCase());
        }
      }
    }
  }

  // Remove duplicates
  return [...new Set(allKeywords)];
}

// Helper function to highlight keywords in plain text
function highlightText(text, keywords) {
  if (!text || !keywords || keywords.length === 0) return text;

  // Escape special regex characters in keywords
  const escapedKeywords = keywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  // Create regex pattern - case insensitive for multi-char keywords
  const pattern = escapedKeywords.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Split by matches and wrap matches with <mark>
  const parts = text.split(regex);

  return parts.map((part, index) => {
    const isMatch = keywords.some(kw =>
      kw.length === 1 ? part === kw : part.toLowerCase() === kw.toLowerCase()
    );
    if (isMatch) {
      return <mark key={index} style={{ backgroundColor: '#ffff00', padding: 0 }}>{part}</mark>;
    }
    return part;
  });
}

// Helper function to highlight keywords in HTML string (for dangerouslySetInnerHTML)
function highlightHTML(htmlString, keywords) {
  if (!htmlString || !keywords || keywords.length === 0) return htmlString;

  // Escape special regex characters in keywords
  const escapedKeywords = keywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  const pattern = escapedKeywords.join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  // Split HTML into tags and text portions
  // This regex matches HTML tags
  const tagRegex = /(<[^>]*>)/g;
  const parts = htmlString.split(tagRegex);

  // Process each part - only highlight in text portions (not tags)
  const highlighted = parts.map(part => {
    if (part.startsWith('<') && part.endsWith('>')) {
      // This is an HTML tag - don't modify
      return part;
    }
    // This is text content - highlight keywords
    return part.replace(regex, '<mark style="background-color:#ffff00;padding:0">$1</mark>');
  });

  return highlighted.join('');
}

function RenderKirkItem ({
  kirk,
  deleteKirk,
  updateKirk,
  resetKirkForm,
  auth,
  isUpdating,
  searchKeywords,
  keyword,
  view,
  htmlResumeRevision
}) {
  const [editField, setEditField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Track pending changes
  const [pendingChanges, setPendingChanges] = useState({});
  const isProcessingRef = useRef(false);

  // For row selection
  const itemColors = useSelector(state => state.fieldButtons.itemColors);
  const dispatch = useDispatch();

  // State for context menu
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuButtonRef = useRef(null);

  // Refs for click-outside-cell confirmation (same as ListDataComponent)
  const pendingCellRef = useRef(null);
  const originalHTMLRef = useRef(null);
  const isConfirmingRef = useRef(false);
  const itemContainerRef = useRef(null);

  // Clear pending changes when kirk data is updated from server (same as ListDataComponent)
  useEffect(() => {
    setPendingChanges(prev => {
      const newPending = { ...prev };
      delete newPending.reason;
      return newPending;
    });
  }, [kirk.reason]);

  useEffect(() => {
    setPendingChanges(prev => {
      const newPending = { ...prev };
      delete newPending.condition;
      return newPending;
    });
  }, [kirk.condition]);

  // Fix iOS Safari stale display bug for input elements in reason/condition fields
  // When iOS Safari suspends a tab for hours, it loses DOM input values completely.
  // Solution: Save input values to localStorage as user types, restore when page becomes visible.
  useEffect(() => {
    const container = itemContainerRef.current;
    if (!container) return;

    const STORAGE_KEY = 'kirkListInputValues';

    const getSavedValues = () => {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      } catch {
        return {};
      }
    };

    const saveInputValue = (inputId, value, isCheckbox = false) => {
      const saved = getSavedValues();
      saved[inputId] = { value, isCheckbox, timestamp: Date.now() };
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      Object.keys(saved).forEach(key => {
        if (saved[key].timestamp < oneWeekAgo) delete saved[key];
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    };

    const getInputId = (input) => {
      const item = input.closest('[data-item-id]');
      if (!item) return null;
      const itemId = item.getAttribute('data-item-id');
      const field = input.closest('[data-field]');
      const fieldName = field ? field.getAttribute('data-field') : 'unknown';
      const allInputsInField = field ? Array.from(field.querySelectorAll('input, textarea, select')) : [];
      const inputIndex = allInputsInField.indexOf(input);
      return `${itemId}_${fieldName}_${inputIndex}`;
    };

    const restoreInputValues = () => {
      const saved = getSavedValues();
      const inputs = container.querySelectorAll(
        'input[type="time"], input[type="date"], input[type="datetime-local"], ' +
        'input[type="text"], input[type="number"], input[type="checkbox"], ' +
        'input[type="radio"], select, textarea'
      );
      inputs.forEach(input => {
        const inputId = getInputId(input);
        if (inputId && saved[inputId]) {
          const { value, isCheckbox } = saved[inputId];
          if (isCheckbox) {
            input.checked = value === 'true' || value === true;
          } else {
            input.value = value;
          }
        }
      });
    };

    const handleInput = (e) => {
      const input = e.target;
      if (!input.matches('input, textarea, select')) return;
      const inputId = getInputId(input);
      if (!inputId) return;
      const isCheckbox = input.type === 'checkbox' || input.type === 'radio';
      const value = isCheckbox ? input.checked : input.value;
      saveInputValue(inputId, value, isCheckbox);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => requestAnimationFrame(() => restoreInputValues()), 100);
      }
    };

    const handlePageShow = () => {
      setTimeout(() => requestAnimationFrame(() => restoreInputValues()), 100);
    };

    container.addEventListener('input', handleInput);
    container.addEventListener('change', handleInput);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    setTimeout(() => restoreInputValues(), 200);

    return () => {
      container.removeEventListener('input', handleInput);
      container.removeEventListener('change', handleInput);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Context menu action handlers
  const handleMenuEdit = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    toggleModal();
  };

  const handleMenuDelete = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    if (window.confirm('Are you sure?')) {
      deleteKirk(kirk._id);
    }
  };

  const handleMenuCopyLink = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
    const viewParam = `&view=${view ? 'list' : 'spreadsheet'}`;
    const linkPath = `/inputs?id=${kirk._id}${keywordParam}${viewParam}`;
    const fullUrl = window.location.origin + linkPath;
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleMenuTogglePin = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setShowContextMenu(false);
    updateKirk(
      kirk._id,
      kirk.date,
      kirk.place,
      kirk.person,
      kirk.subject,
      kirk.reason,
      kirk.condition,
      kirk.data,
      kirk.unit,
      !kirk.pinned
    );
  };

  // Click-outside handler to close menu
  useEffect(() => {
    if (!showContextMenu) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.context-menu')) {
        setShowContextMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [showContextMenu]);

  const toggleModal = () => setIsOpen(!isOpen);

  const handleSubmit = (value, event) => {
    toggleModal();
    if (window.confirm('OK to change data?')) {
      updateKirk(
        kirk._id,
        value.date,
        value.place,
        value.person,
        value.subject,
        value.reason,
        value.condition,
        value.data,
        value.unit
      );
    }
  };

  // Menu button click handler
  const handleMenuButtonClick = (event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      x: rect.left,
      y: rect.bottom + 4
    });
    setShowContextMenu(true);
  };

  // Row click handler for selection (no clipboard copy)
  const handleClick = (event, id) => {
    if (event.target.closest('details,button,input,select,textarea,a,.context-menu')) return;

    const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
    const viewParam = `&view=${view ? 'list' : 'spreadsheet'}`;
    const linkPath = `/inputs?id=${id}${keywordParam}${viewParam}`;

    window.history.pushState({}, '', linkPath);

    const newColor = itemColors[id] === '#d6eaf8' ? 'white' : '#d6eaf8';
    dispatch({
      type: 'SET_ITEM_COLOR',
      payload: { id, color: newColor }
    });

    if (newColor === '#d6eaf8') {
      dispatch(actions.change('input.date', kirk.date));
      dispatch(actions.change('input.place', kirk.place));
      dispatch(actions.change('input.person', kirk.person));
      dispatch(actions.change('input.subject', kirk.subject));
      dispatch(actions.change('input.reason', kirk.reason));
      dispatch(actions.change('input.condition', kirk.condition));
      dispatch(actions.change('input.data', kirk.data));
      dispatch(actions.change('input.unit', kirk.unit));

      const formData = {
        date: kirk.date || '',
        place: kirk.place || '',
        person: kirk.person || '',
        subject: kirk.subject || '',
        reason: kirk.reason || '',
        condition: kirk.condition || '',
        data: kirk.data || '',
        unit: kirk.unit || ''
      };
      localStorage.setItem('dataForm', JSON.stringify(formData));
    } else {
      dispatch(actions.change('input.date', ''));
      dispatch(actions.change('input.place', ''));
      dispatch(actions.change('input.person', ''));
      dispatch(actions.change('input.subject', ''));
      dispatch(actions.change('input.reason', ''));
      dispatch(actions.change('input.condition', ''));
      dispatch(actions.change('input.data', ''));
      dispatch(actions.change('input.unit', ''));

      localStorage.removeItem('dataForm');
    }
  };

  // 編集開始
  const handleDoubleClick = (field, value) => {
    setEditField(field);
    setTempValue(value);
  };

  // 編集内容変更
  const handleChange = e => {
    const newValue = e.target.value;
    const prevValue = tempValue; // Capture previous value before update
    setTempValue(newValue);

    // HTML tag auto-completion for reason/condition fields (works for both textarea and input)
    const fieldName = e.target.name;
    if (fieldName === 'reason' || fieldName === 'condition') {
      handleHtmlTagAutoComplete(e.target, newValue, setTempValue, prevValue);
      handleArithmeticInput(e.target, newValue, setTempValue, prevValue);
    }
  };

  // 編集終了・保存
  const handleBlur = field => {
    const original = kirk[field] ?? '';
    if (tempValue === original) {
      setEditField(null);
      return;
    }
    isConfirmingRef.current = true;
    const confirmed = window.confirm('OK to change data?');
    // Reset after a short delay to allow click event from dialog to pass
    setTimeout(() => {
      isConfirmingRef.current = false;
    }, 150);

    if (confirmed) {
      updateKirk(
        kirk._id,
        field === 'date' ? tempValue : kirk.date,
        field === 'place' ? tempValue : kirk.place,
        field === 'person' ? tempValue : kirk.person,
        field === 'subject' ? tempValue : kirk.subject,
        field === 'reason' ? tempValue : kirk.reason,
        field === 'condition' ? tempValue : kirk.condition,
        field === 'data' ? tempValue : kirk.data,
        field === 'unit' ? tempValue : kirk.unit
      );
    }
    setEditField(null);
  };

  // Handle HTML tag auto-completion for modal Control.textarea fields
  const handleModalTagAutoComplete = e => {
    const textarea = e.target;
    const value = textarea.value;
    const cursorPos = textarea.selectionStart;

    // Arithmetic evaluation (e.g. "1+1=" -> "1+1=2")
    handleArithmeticInput(textarea, value, newVal => {
      textarea.value = newVal;
      const inputEvent = new Event('input', { bubbles: true });
      textarea.dispatchEvent(inputEvent);
    });

    const result = checkHtmlTagAutoComplete(value, cursorPos);
    if (result) {
      textarea.value = result.newValue;
      const event = new Event('input', { bubbles: true });
      textarea.dispatchEvent(event);
      setTimeout(() => {
        textarea.selectionStart = result.cursorPos;
        textarea.selectionEnd = result.cursorPos;
        textarea.focus();
      }, 0);
    }
  };

  // Extract values from input/select elements and rebuild HTML with values
  const updateHtmlWithValues = element => {
    const clone = element.cloneNode(true);

    // Update all input elements with their current values
    const inputs = clone.querySelectorAll('input');
    inputs.forEach((input, index) => {
      const originalInput = element.querySelectorAll('input')[index];
      if (originalInput) {
        if (input.type === 'checkbox' || input.type === 'radio') {
          if (originalInput.checked) {
            input.setAttribute('checked', 'checked');
          } else {
            input.removeAttribute('checked');
          }
        } else {
          if (originalInput.value) {
            input.setAttribute('value', originalInput.value);
          } else {
            input.removeAttribute('value');
          }
        }
      }
    });

    // Update all select elements with their current values
    const selects = clone.querySelectorAll('select');
    selects.forEach((select, index) => {
      const originalSelect = element.querySelectorAll('select')[index];
      if (originalSelect) {
        // Remove all selected attributes first
        const options = select.querySelectorAll('option');
        options.forEach(opt => opt.removeAttribute('selected'));

        // Set the selected attribute on the currently selected option
        const selectedIndex = originalSelect.selectedIndex;
        if (selectedIndex >= 0 && options[selectedIndex]) {
          options[selectedIndex].setAttribute('selected', 'selected');
        }
      }
    });

    // Update all textarea elements with their current values
    const textareas = clone.querySelectorAll('textarea');
    textareas.forEach((textarea, index) => {
      const originalTextarea = element.querySelectorAll('textarea')[index];
      if (originalTextarea) {
        textarea.textContent = originalTextarea.value;
      }
    });

    return clone.innerHTML;
  };

  // Click-outside-cell confirmation handler (same logic as ListDataComponent)
  useEffect(() => {
    const startTrackingCell = (element, field) => {
      if (!pendingCellRef.current || pendingCellRef.current.element !== element) {
        originalHTMLRef.current = element.innerHTML;
        pendingCellRef.current = { element, field };
      }
    };

    // Track when user interacts with form elements inside contentEditable divs
    const handleFocusIn = e => {
      // Check if target is a contentEditable div itself
      if (e.target && e.target.classList && e.target.classList.contains('my-styled-div') &&
          e.target.contentEditable === 'true') {
        const field = e.target.dataset.field;
        if (field) {
          startTrackingCell(e.target, field);
        }
        return;
      }

      // Check if target is an INPUT/SELECT/TEXTAREA inside a contentEditable div
      if (!e.target || !['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) {
        return;
      }

      const editableDiv = e.target.closest('.my-styled-div[contenteditable="true"]');
      if (!editableDiv) return;

      const field = editableDiv.dataset.field;
      if (!field) return;

      startTrackingCell(editableDiv, field);
    };

    // Track input on contentEditable divs (for direct text editing)
    const handleInput = e => {
      if (e.target && e.target.classList && e.target.classList.contains('my-styled-div') &&
          e.target.contentEditable === 'true') {
        const field = e.target.dataset.field;
        if (field) {
          e.target.dataset.modified = 'true';
          startTrackingCell(e.target, field);
        }
      }
    };

    // Track changes on checkboxes/radios/selects (they may not trigger focusin on iOS)
    const handleChange = e => {
      if (!e.target || !['INPUT', 'SELECT'].includes(e.target.tagName)) {
        return;
      }

      const editableDiv = e.target.closest('.my-styled-div[contenteditable="true"]');
      if (!editableDiv) return;

      const field = editableDiv.dataset.field;
      if (!field) return;

      // Mark as modified
      editableDiv.dataset.modified = 'true';
      startTrackingCell(editableDiv, field);
    };

    // Handle click/touch outside to confirm changes
    const handleClickOutside = e => {
      if (isConfirmingRef.current) return;
      if (!pendingCellRef.current) return;

      const { element, field } = pendingCellRef.current;

      // Check if click is inside the same cell
      if (element.contains(e.target)) {
        return; // Still inside the cell, don't confirm yet
      }

      // Check if content was modified
      if (element.dataset.modified !== 'true') {
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
        return;
      }

      // Click is outside the cell - sync and confirm
      const updatedHtml = updateHtmlWithValues(element);
      const original = kirk[field] ?? '';

      if (updatedHtml === original) {
        pendingCellRef.current = null;
        originalHTMLRef.current = null;
        element.dataset.modified = 'false';
        return;
      }

      isConfirmingRef.current = true;

      if (window.confirm('OK to change data?')) {
        // Store in pending changes - this will be shown immediately via createRefCallback
        // useEffect will clear it when kirk data updates from server
        setPendingChanges(prev => ({ ...prev, [field]: updatedHtml }));
        // Save succeeded - this cell is no longer "being edited", so future
        // renders can resync its DOM from the (now updating) kirk data again.
        element.dataset.modified = 'false';

        // Send update to database
        updateKirk(
          kirk._id,
          field === 'date' ? updatedHtml : kirk.date,
          field === 'place' ? updatedHtml : kirk.place,
          field === 'person' ? updatedHtml : kirk.person,
          field === 'subject' ? updatedHtml : kirk.subject,
          field === 'reason' ? updatedHtml : kirk.reason,
          field === 'condition' ? updatedHtml : kirk.condition,
          field === 'data' ? updatedHtml : kirk.data,
          field === 'unit' ? updatedHtml : kirk.unit
        );
      } else {
        // User cancelled - restore original
        element.innerHTML = originalHTMLRef.current;
        element.dataset.modified = 'false';
      }

      isConfirmingRef.current = false;
      pendingCellRef.current = null;
      originalHTMLRef.current = null;
    };

    const container = itemContainerRef.current;
    if (container) {
      container.addEventListener('focusin', handleFocusIn, true);
      container.addEventListener('input', handleInput, true);
      container.addEventListener('change', handleChange, true);
    }

    // Listen for clicks/touches on the entire document to detect "click outside"
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);

    return () => {
      if (container) {
        container.removeEventListener('focusin', handleFocusIn, true);
        container.removeEventListener('input', handleInput, true);
        container.removeEventListener('change', handleChange, true);
      }
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [kirk, updateKirk]);

  // Handle blur - no-op, confirmation is handled by click-outside
  const handleHtmlBlur = (field, e) => {
    // No-op - confirmation is now handled by click-outside handler
  };

  // Custom ref callback to prevent overwriting content
  const createRefCallback = field => element => {
    if (!element) return;

    // Only reason/condition get Markdown rendering - place/data/unit etc.
    // (also routed through this callback) keep rendering as raw HTML only.
    const shouldRenderMarkdown = field === 'reason' || /^condition\d*$/.test(field);
    const toHtml = value => shouldRenderMarkdown ? renderMarkdown(value) : value;

    // If there's a pending change, always show it (optimistic UI)
    if (pendingChanges[field] !== undefined) {
      const highlightedContent = highlightHTML(toHtml(pendingChanges[field]), searchKeywords);
      if (element.innerHTML !== highlightedContent) {
        element.innerHTML = DOMPurify.sanitize(highlightedContent);
      }
      return;
    }

    // Only set innerHTML from kirk data if not currently being edited
    if (!element.dataset.modified || element.dataset.modified === 'false') {
      const highlightedContent = highlightHTML(toHtml(kirk[field]), searchKeywords);
      if (element.innerHTML !== highlightedContent) {
        element.innerHTML = DOMPurify.sanitize(highlightedContent);
      }
    }
  };

  // Helper to extract domain name from URL
  const extractDomainName = url => {
    try {
      const parts = url.split('//')[1].split('/')[0].split('.');
      if (url.indexOf('www') !== -1) {
        // If has 'www', use second part
        return parts[1];
      } else if (parts.length === 2) {
        // If 2 parts, use first part
        return parts[0];
      } else {
        // Use whichever is longer between first and second
        return parts[0].length < parts[1].length ? parts[1] : parts[0];
      }
    } catch (e) {
      return url;
    }
  };

  // Render HTML field with inline editing
  const renderHtmlField = (field, value) => {
    if (editField === field) {
      return (
        <textarea
          name={field}
          value={tempValue}
          onChange={handleChange}
          onBlur={() => handleBlur(field)}
          onDoubleClick={selectImageTagOnDoubleClick}
          autoFocus
          style={{ width: '100%', minHeight: 60 }}
        />
      );
    } else {
      const displayValue = pendingChanges[field] ?? value;

      return (
        <div
          className='my-styled-div'
          ref={createRefCallback(field)}
          key={`${kirk._id}-${field}-${displayValue}-${htmlResumeRevision}`}
          data-field={field}
          onDoubleClick={() =>
            auth.id === kirk.user && handleDoubleClick(field, displayValue)
          }
          contentEditable={auth.id === kirk.user}
          suppressContentEditableWarning={true}
          data-modified='false'
          onBlur={e => {
            if (auth.id === kirk.user) {
              handleHtmlBlur(field, e);
            }
          }}
          onInput={e => {
            if (auth.id === kirk.user) {
              e.currentTarget.dataset.modified = 'true';
            }
          }}
          onPaste={e => {
            if (auth.id === kirk.user) {
              const cellElement = e.currentTarget;
              handleImagePaste(e, () => {
                cellElement.dataset.modified = 'true';
              });
            }
          }}
          onClick={e => {
            // This div is contentEditable for the record's owner, and browsers
            // suppress a link's default navigation on click inside an editable
            // region (so the click just places the caret instead) - so <a> tags
            // typed into reason/condition never went anywhere. Take over here
            // and open the link explicitly, bypassing that suppression.
            const anchor = e.target.closest('a');
            if (anchor) {
              const href = anchor.getAttribute('href');
              if (href) {
                e.preventDefault();
                e.stopPropagation();
                window.open(href, anchor.getAttribute('target') || '_blank', 'noopener,noreferrer');
              }
              return;
            }
            if (e.target.tagName === 'IMG') {
              e.preventDefault();
              e.stopPropagation();
              setLightboxImage({
                src: e.target.src,
                width: e.target.offsetWidth,
                height: e.target.offsetHeight
              });
            }
          }}
        />
      );
    }
  };

  // Helper to render fields with link detection (for place, data, conditions)
  const renderFieldWithLinkDetection = (
    fieldName,
    value,
    showAsCode = false
  ) => {
    if (value === '') return '';

    // Check if it's a URL or file path
    if (value.indexOf('http') === 0 || value.indexOf('/') === 0) {
      if (value.indexOf('http') === 0) {
        return (
          <a href={value} target='_blank' rel='noreferrer'>
            <span>{extractDomainName(value)}</span>
          </a>
        );
      } else {
        return (
          <a href={`file:${value}`} target='_blank' rel='noreferrer'>
            <span className='fa fa-folder-open'></span>
          </a>
        );
      }
    } else {
      // It's HTML content with potential input/select elements or plain text
      if (showAsCode) {
        return <code>{highlightText(value, searchKeywords)}</code>;
      } else {
        return renderHtmlField(fieldName, value);
      }
    }
  };

  // フィールドごとの表示・編集UI
  const renderField = (field, value, isHtml = false) => {
    if (editField === field) {
      // textarea for reason/condition, input for others
      if (field === 'reason' || field === 'condition') {
        return (
          <textarea
            value={tempValue}
            onChange={handleChange}
            onBlur={() => handleBlur(field)}
            autoFocus
            style={{ width: '100%', minHeight: 60 }}
          />
        );
      } else {
        return (
          <input
            type='text'
            value={tempValue}
            onChange={handleChange}
            onBlur={() => handleBlur(field)}
            autoFocus
            style={{ width: '100%' }}
          />
        );
      }
    } else {
      if (isHtml) {
        return renderHtmlField(field, value);
      } else {
        return (
          <div
            className='my-styled-div'
            onDoubleClick={() =>
              auth.id === kirk.user && handleDoubleClick(field, value)
            }
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {highlightText(value, searchKeywords)}
          </div>
        );
      }
    }
  };

  return (
    <div ref={itemContainerRef}>
      <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      {/* Context Menu */}
      {showContextMenu && createPortal(
        <div className="context-menu" style={{
          position: 'fixed', left: menuPosition.x, top: menuPosition.y,
          backgroundColor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 1000, fontSize: '13px',
          userSelect: 'none', minWidth: '180px', overflow: 'hidden',
          animation: 'menuScaleIn 150ms cubic-bezier(0.34, 1.56, 0.64, 1)', transformOrigin: 'top left'
        }} onClick={e => e.stopPropagation()}>
          <style>{`
            @keyframes menuScaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
            .context-menu-item:hover { background-color: #f0f0f0; }
            .context-menu-item.delete-item:hover { background-color: rgba(163, 45, 45, 0.08); }
          `}</style>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e8e8e8', backgroundColor: '#f8f9fa',
            fontSize: '11px', color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {kirk.subject ? (kirk.subject.length > 25 ? kirk.subject.substring(0, 25) + '...' : kirk.subject) : 'Record'}
          </div>
          {auth.id === kirk.user && (
            <div className="context-menu-item" style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', transition: 'background-color 100ms'
            }} onClick={() => { handleMenuEdit(); setShowContextMenu(false); }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit record
              </span>
              <span style={{ fontSize: '10px', fontFamily: 'monospace', backgroundColor: '#e8e8e8', padding: '2px 6px', borderRadius: '3px', color: '#666' }}>E</span>
            </div>
          )}
          <div className="context-menu-item" style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', transition: 'background-color 100ms'
          }} onClick={() => { handleMenuCopyLink(); setShowContextMenu(false); }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Copy link
            </span>
            <span style={{ fontSize: '10px', fontFamily: 'monospace', backgroundColor: '#e8e8e8', padding: '2px 6px', borderRadius: '3px', color: '#666' }}>C</span>
          </div>
          {auth.id === kirk.user && (
            <div className="context-menu-item" style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', transition: 'background-color 100ms'
            }} onClick={() => { handleMenuTogglePin(); setShowContextMenu(false); }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z" />
                  <line x1="12" y1="16" x2="12" y2="22" />
                </svg>
                {kirk.pinned ? 'Unpin' : 'Pin to top'}
              </span>
            </div>
          )}
          {auth.id === kirk.user && (
            <>
              <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '4px 0' }} />
              <div className="context-menu-item delete-item" style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', color: '#A32D2D', transition: 'background-color 100ms'
              }} onClick={() => { handleMenuDelete(); setShowContextMenu(false); }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </span>
                <span style={{ fontSize: '10px', fontFamily: 'monospace', backgroundColor: 'rgba(163, 45, 45, 0.12)', padding: '2px 6px', borderRadius: '3px', color: '#A32D2D' }}>D</span>
              </div>
            </>
          )}
        </div>,
        document.body
      )}

    <ListGroup>
      <ListGroupItem
        data-item-id={kirk._id}
        onClick={event => handleClick(event, kirk._id)}
        style={{ backgroundColor: itemColors[kirk._id] || 'white', cursor: 'pointer' }}
      >
        <button
          ref={menuButtonRef}
          onClick={handleMenuButtonClick}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 6px',
            fontSize: '16px',
            color: '#666',
            borderRadius: '4px',
            lineHeight: 1
          }}
          title="More actions"
        >
          ☰
        </button>
        <Modal
          isOpen={isOpen}
          toggle={toggleModal}
          size='lg'
          style={{ maxWidth: '1000px', width: '100%' }}
          className='my-modal'
        >
          <ModalHeader
            style={{
              borderBottom: '1px solid #c5d8ec',
              padding: '12px 16px'
            }}
            close={
              <button
                type="button"
                onClick={toggleModal}
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '6px',
                  background: 'rgba(255,255,255,0.6)',
                  border: 'none',
                  color: '#4a6a8a',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            }
          >
            <span style={{ fontWeight: 500, fontSize: '15px' }}>Update Kirk</span>
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit} model='input' onClick={e => e.stopPropagation()}>
              <Row className='form-group mb-3'>
                <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Control.text
                      model='.date'
                      id={`edit-date-${kirk._id}`}
                      name='edit-date'
                      defaultValue={kirk.date}
                      className='form-control'
                      style={{
                        paddingTop: '20px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        height: 'auto',
                        minHeight: '48px',
                        background: '#ffffff',
                        border: '0.5px solid #c5d8ec',
                        borderRadius: '8px'
                      }}
                    />
                    <label
                      htmlFor={`edit-date-${kirk._id}`}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '6px',
                        fontSize: '10px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        color: '#185FA5',
                        pointerEvents: 'none'
                      }}
                    >
                      Date
                    </label>
                  </div>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Control.text
                      model='.place'
                      id={`edit-place-${kirk._id}`}
                      name='edit-place'
                      defaultValue={kirk.place}
                      className='form-control'
                      style={{
                        paddingTop: '20px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        height: 'auto',
                        minHeight: '48px',
                        background: '#ffffff',
                        border: '0.5px solid #c5d8ec',
                        borderRadius: '8px'
                      }}
                    />
                    <label
                      htmlFor={`edit-place-${kirk._id}`}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '6px',
                        fontSize: '10px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        color: '#185FA5',
                        pointerEvents: 'none'
                      }}
                    >
                      Place
                    </label>
                  </div>
                </div>
              </Row>
              <Row className='form-group mb-3'>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Control.text
                    model='.person'
                    id={`edit-person-${kirk._id}`}
                    name='edit-person'
                    defaultValue={kirk.person}
                    className='form-control'
                    style={{
                      paddingTop: '20px',
                      paddingBottom: '6px',
                      paddingLeft: '12px',
                      height: 'auto',
                      minHeight: '48px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px'
                    }}
                  />
                  <label
                    htmlFor={`edit-person-${kirk._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '6px',
                      fontSize: '10px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      color: '#185FA5',
                      pointerEvents: 'none'
                    }}
                  >
                    Person
                  </label>
                </div>
              </Row>
              <Row className='form-group mb-3'>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Control.text
                    model='.subject'
                    id={`edit-subject-${kirk._id}`}
                    name='edit-subject'
                    defaultValue={kirk.subject}
                    className='form-control'
                    style={{
                      paddingTop: '20px',
                      paddingBottom: '6px',
                      paddingLeft: '12px',
                      height: 'auto',
                      minHeight: '48px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px'
                    }}
                  />
                  <label
                    htmlFor={`edit-subject-${kirk._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '6px',
                      fontSize: '10px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      color: '#185FA5',
                      pointerEvents: 'none'
                    }}
                  >
                    Subject
                  </label>
                </div>
              </Row>
              <Row className='form-group mb-3'>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Control.textarea
                    model='.reason'
                    id={`edit-reason-${kirk._id}`}
                    name='edit-reason'
                    defaultValue={kirk.reason}
                    className='form-control'
                    rows={3}
                    style={{
                      paddingTop: '24px',
                      paddingBottom: '8px',
                      paddingLeft: '12px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px',
                      resize: 'vertical'
                    }}
                    onChange={handleModalTagAutoComplete}
                  />
                  <label
                    htmlFor={`edit-reason-${kirk._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '6px',
                      fontSize: '10px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      color: '#185FA5',
                      pointerEvents: 'none'
                    }}
                  >
                    Reason
                  </label>
                </div>
              </Row>
              <Row className='form-group mb-3'>
                <div style={{ position: 'relative', width: '100%' }}>
                  <Control.textarea
                    model='.condition'
                    id={`edit-condition-${kirk._id}`}
                    name='edit-condition'
                    defaultValue={kirk.condition}
                    className='form-control'
                    rows={2}
                    style={{
                      paddingTop: '24px',
                      paddingBottom: '8px',
                      paddingLeft: '12px',
                      background: '#ffffff',
                      border: '0.5px solid #c5d8ec',
                      borderRadius: '8px',
                      resize: 'vertical'
                    }}
                    onChange={handleModalTagAutoComplete}
                  />
                  <label
                    htmlFor={`edit-condition-${kirk._id}`}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '6px',
                      fontSize: '10px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      color: '#185FA5',
                      pointerEvents: 'none'
                    }}
                  >
                    Condition
                  </label>
                </div>
              </Row>
              <Row className='form-group mb-3'>
                <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Control.text
                      model='.data'
                      id={`edit-data-${kirk._id}`}
                      name='edit-data'
                      defaultValue={kirk.data}
                      className='form-control'
                      style={{
                        paddingTop: '20px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        height: 'auto',
                        minHeight: '48px',
                        background: '#ffffff',
                        border: '0.5px solid #c5d8ec',
                        borderRadius: '8px'
                      }}
                    />
                    <label
                      htmlFor={`edit-data-${kirk._id}`}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '6px',
                        fontSize: '10px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        color: '#185FA5',
                        pointerEvents: 'none'
                      }}
                    >
                      Data
                    </label>
                  </div>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Control.text
                      model='.unit'
                      id={`edit-unit-${kirk._id}`}
                      name='edit-unit'
                      defaultValue={kirk.unit}
                      className='form-control'
                      style={{
                        paddingTop: '20px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        height: 'auto',
                        minHeight: '48px',
                        background: '#ffffff',
                        border: '0.5px solid #c5d8ec',
                        borderRadius: '8px'
                      }}
                    />
                    <label
                      htmlFor={`edit-unit-${kirk._id}`}
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '6px',
                        fontSize: '10px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        color: '#185FA5',
                        pointerEvents: 'none'
                      }}
                    >
                      Unit
                    </label>
                  </div>
                </div>
              </Row>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button
                  type='button'
                  onClick={toggleModal}
                  style={{
                    background: 'rgba(255,255,255,0.6)',
                    border: '0.5px solid #c5d8ec',
                    color: '#4a6a8a',
                    fontWeight: 500,
                    padding: '7px 22px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  style={{
                    background: '#185FA5',
                    border: 'none',
                    color: '#fff',
                    fontWeight: 500,
                    padding: '7px 22px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Update
                </button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
        {'  '}{highlightText(kirk.date, searchKeywords)}{' '}
        {kirk.place === ''
          ? ''
          : [
              renderFieldWithLinkDetection('place', kirk.place, false),
              <br key='br-place' />
            ]}
        {renderField('person', kirk.person)}
        {kirk.subject === '' ? '' : <b>{highlightText(kirk.subject, searchKeywords)}</b>}
        {renderField('reason', kirk.reason, true)}
        {renderField('condition', kirk.condition, true)}
        {kirk.data === ''
          ? ''
          : [renderFieldWithLinkDetection('data', kirk.data, true), ' ']}
        {renderField('unit', kirk.unit)}
      </ListGroupItem>
    </ListGroup>
    </div>
  );
}

export let num;

export default function ListKirk2 (props) {
  const [pinEnabled, setPinEnabled] = usePinEnabled();
  const [currentPage, setCurrentPage] = useState(1);
  const [kirkListPerPage] = useState(100);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(null);
  const [savedPage, setSavedPage] = useState(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const deepLinkHandledIdRef = useRef(null);
  // Computed during render (see pageById below); applied in an effect rather
  // than during render itself, so it isn't affected by StrictMode
  // double-invoking the render body.
  const pendingDeepLinkPageRef = useRef(null);

  useEffect(() => {
    const pending = pendingDeepLinkPageRef.current;
    if (pending && deepLinkHandledIdRef.current !== pending.id) {
      deepLinkHandledIdRef.current = pending.id;
      setCurrentPage(pending.page);
    }
  });

  // Deep linking: scroll to and highlight the item when URL has ?id=
  // parameter. Which page to jump to is figured out live during render (see
  // pageById below) rather than from a stored page number, since a fixed
  // page number would go stale as new records shift everything's position.
  useEffect(() => {
    const itemId = searchParams.get('id');
    if (itemId && !props.isLoading) {
      // Poll for the item instead of a fixed delay - re-sorting/paginating a
      // large list can take longer than any one fixed timeout to reach the DOM.
      let attempts = 0;
      const findAndScrollToItem = () => {
        const item = document.querySelector(`[data-item-id="${itemId}"]`);
        if (item) {
          item.scrollIntoView({ behavior: 'smooth', block: 'center' });
          dispatch({
            type: 'SET_ITEM_COLOR',
            payload: { id: itemId, color: '#fffacd' }
          });
          setTimeout(() => {
            dispatch({
              type: 'SET_ITEM_COLOR',
              payload: { id: itemId, color: '#d6eaf8' }
            });
          }, 500);
        } else if (attempts < 20) {
          attempts += 1;
          setTimeout(findAndScrollToItem, 100);
        }
      };
      setTimeout(findAndScrollToItem, 100);
    }
  }, [searchParams, props.isLoading, dispatch]);

  // Reset to page 1 whenever the search keyword actually changes, so a
  // search that only matches items on an earlier page doesn't leave the view
  // stuck on a now-empty page. Compares against the previous value (rather
  // than a "have I run once" flag) so it stays a no-op on mount even under
  // StrictMode's double-invoked effects, and doesn't clobber a deep link's
  // page jump.
  const previousKeywordRef = useRef(props.keyword);
  useEffect(() => {
    if (previousKeywordRef.current !== props.keyword) {
      setCurrentPage(1);
    }
    previousKeywordRef.current = props.keyword;
  }, [props.keyword]);

  // Sorting selectors from Redux (synced with ListData)
  const toggledInputFieldButton = useSelector(
    state => state.fieldButtons.toggledInputFieldButton
  );
  const isInputIdAscending = useSelector(
    state => state.fieldButtons.isInputIdAscending
  );
  const isInputDateAscending = useSelector(
    state => state.fieldButtons.isInputDateAscending
  );
  const isInputPlaceAscending = useSelector(
    state => state.fieldButtons.isInputPlaceAscending
  );
  const isInputPersonAscending = useSelector(
    state => state.fieldButtons.isInputPersonAscending
  );
  const isInputSubjectAscending = useSelector(
    state => state.fieldButtons.isInputSubjectAscending
  );
  const isInputReasonAscending = useSelector(
    state => state.fieldButtons.isInputReasonAscending
  );
  const isInputConditionAscending = useSelector(
    state => state.fieldButtons.isInputConditionAscending
  );
  const isInputDataAscending = useSelector(
    state => state.fieldButtons.isInputDataAscending
  );
  const isInputUnitAscending = useSelector(
    state => state.fieldButtons.isInputUnitAscending
  );

  // Date conversion function (exactly same as ListData)
  const conv = (inputDate) => {
    let day = inputDate.split(' ').find(element => element.includes('-'));
    if (day) {
      const len = day.split('-')[0].length;

      if (!day.split('-')[0]) {
        return '';
      }

      if (!day.split('-')[1]) {
        return '';
      }

      if (!day.split('-')[2]) {
        return '';
      }

      const firstnumber = day.split('-')[0].padStart(2, '0');
      const secondnumber = day.split('-')[1].padStart(2, '0');
      const thirdnumber = day.split('-')[2].padStart(2, '0');

      let time = inputDate.split(' ').find(element => element.includes(':'));
      if (!time) {
        if (len === 1 || len === 2) {
          day = `${secondnumber}-${thirdnumber}-${firstnumber} 00:00:00`;
        } else {
          day = `${firstnumber}-${secondnumber}-${thirdnumber} 00:00:00`;
        }
      } else {
        const [hour, minute] = time
          .split(':')
          .map(num => num.padStart(2, '0'));
        if (len === 1 || len === 2) {
          day = `${secondnumber}-${thirdnumber}-${firstnumber} ${hour}:${minute}:00`;
        } else {
          day = `${firstnumber}-${secondnumber}-${thirdnumber} ${hour}:${minute}:00`;
        }
      }
    }

    if (!day) {
      return '';
    }
    return day;
  };

  // Apply sorting based on ListData field buttons (exactly same logic as ListData)
  const applySorting = useCallback((data) => {
    const sorted = [...data];

    switch (toggledInputFieldButton) {
      case 'ID':
        if (isInputIdAscending === false) {
          sorted.sort((a, b) => a._id.localeCompare(b._id));
        } else {
          sorted.sort((a, b) => b._id.localeCompare(a._id));
        }
        break;
      case 'DATE':
        if (isInputDateAscending === false) {
          sorted.sort((a, b) => conv(a.date).localeCompare(conv(b.date)));
        } else {
          sorted.sort((a, b) => conv(b.date).localeCompare(conv(a.date)));
        }
        break;
      case 'PLACE':
        if (isInputPlaceAscending === false) {
          sorted.sort((a, b) => a.place.localeCompare(b.place));
        } else {
          sorted.sort((a, b) => b.place.localeCompare(a.place));
        }
        break;
      case 'PERSON':
        if (isInputPersonAscending === false) {
          sorted.sort((a, b) => a.person.localeCompare(b.person));
        } else {
          sorted.sort((a, b) => b.person.localeCompare(a.person));
        }
        break;
      case 'SUBJECT':
        if (isInputSubjectAscending === false) {
          sorted.sort((a, b) => a.subject.localeCompare(b.subject));
        } else {
          sorted.sort((a, b) => b.subject.localeCompare(a.subject));
        }
        break;
      case 'REASON':
        if (isInputReasonAscending === false) {
          sorted.sort((a, b) => a.reason.localeCompare(b.reason));
        } else {
          sorted.sort((a, b) => b.reason.localeCompare(a.reason));
        }
        break;
      case 'CONDITION':
        if (isInputConditionAscending === false) {
          sorted.sort((a, b) => a.condition.localeCompare(b.condition));
        } else {
          sorted.sort((a, b) => b.condition.localeCompare(a.condition));
        }
        break;
      case 'DATA':
        if (isInputDataAscending === false) {
          sorted.sort((a, b) => a.data.replaceAll(',', '').localeCompare(b.data.replaceAll(',', ''), undefined, { numeric: true }));
        } else {
          sorted.sort((a, b) => b.data.replaceAll(',', '').localeCompare(a.data.replaceAll(',', ''), undefined, { numeric: true }));
        }
        break;
      case 'UNIT':
        if (isInputUnitAscending === false) {
          sorted.sort((a, b) => a.unit.localeCompare(b.unit));
        } else {
          sorted.sort((a, b) => b.unit.localeCompare(a.unit));
        }
        break;
      default:
        // Default: same as ID case
        if (isInputIdAscending === false) {
          sorted.sort((a, b) => a._id.localeCompare(b._id));
        } else {
          sorted.sort((a, b) => b._id.localeCompare(a._id));
        }
    }

    // Show newest-first, then float pinned items to the very top regardless
    // of whatever column/direction is currently sorted (unless pinning is
    // switched off, in which case just show the plain sort order)
    return pinEnabled ? orderWithPinnedFirst(sorted.reverse()) : sorted.reverse();
  }, [toggledInputFieldButton, isInputIdAscending, isInputDateAscending, isInputPlaceAscending,
      isInputPersonAscending, isInputSubjectAscending, isInputReasonAscending,
      isInputConditionAscending, isInputDataAscending, isInputUnitAscending, pinEnabled]);

  const paginate = pageNumber => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show/hide scroll-to-top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled down more than 1000px
      setShowScrollButton(window.scrollY > 1000);
      // Consider "at top" when under 1000px threshold
      setIsAtTop(window.scrollY < 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle between top and saved position
  const toggleScrollPosition = () => {
    if (isAtTop && savedScrollPosition !== null) {
      // DOWN arrow clicked - go back to the saved page and scroll position
      if (savedPage !== null && savedPage !== currentPage) {
        setCurrentPage(savedPage);
        // Wait for the restored page's content to render before scrolling
        // to a non-zero offset, or the browser clamps to the old content height
        setTimeout(() => {
          window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
        }, 100);
      } else {
        window.scrollTo({
          top: savedScrollPosition,
          behavior: 'smooth'
        });
      }
      setSavedScrollPosition(null);
      setSavedPage(null);
    } else {
      // UP arrow clicked - go to the very top of the list (page 1), not just
      // the top of whichever page happens to be showing
      if (savedScrollPosition === null) {
        // Only save position if none exists (keep previous position)
        setSavedScrollPosition(window.scrollY);
        setSavedPage(currentPage);
      }
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Floating button styles - simple arrow, no circle
  const shouldShowButton = showScrollButton || (isAtTop && savedScrollPosition !== null);
  const scrollButtonStyle = {
    position: 'fixed',
    top: '70px',
    right: '20px',
    zIndex: 1000,
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    cursor: 'pointer',
    display: shouldShowButton ? 'block' : 'none',
    fontSize: '28px',
    padding: '10px',
    transition: 'opacity 0.3s'
  };

  if (props.isLoading) {
    return (
      <div className='container'>
        <div className='row'>
          <Loading />
        </div>
      </div>
    );
  } else if (props.errMess) {
    return (
      <div className='container'>
        <div className='row'>
          <h4>{props.errMess}</h4>
        </div>
      </div>
    );
  } else if (props.keyword !== '') {
    const keywordAndArray = props.keyword.split('|');
    var keywords = [];
    var exwords = [];
    for (let k = 0; k < keywordAndArray.length; k++) {
      let keywordArray = keywordAndArray[k].split(/[\s\u3000]+/);
      var keywords_temp = [];
      var exwords_temp = [];
      let j = 0;
      let m = 0;
      for (let i = 0; i < keywordArray.length; i++) {
        if ((keywordArray[i] !== ' ') & (keywordArray[i] !== '')) {
          if (keywordArray[i].split('')[0] === '!') {
            if (keywordArray[i].length === 2) {
              exwords_temp[m] = keywordArray[i].split('').splice(1).join('');
            } else {
              exwords_temp[m] = keywordArray[i]
                .toLowerCase()
                .split('')
                .splice(1)
                .join('');
            }
            m = m + 1;
          } else {
            if (keywordArray[i].length === 1) {
              keywords_temp[j] = keywordArray[i];
            } else {
              keywords_temp[j] = keywordArray[i].toLowerCase();
            }
            j = j + 1;
          }
        }
      }
      keywords.push(keywords_temp);
      exwords.push(exwords_temp);
    }

    var foundKirks = [];
    for (let i = 0; i < keywords.length; i++) {
      const foundKirks_temp = props.kirks.filter(curr => {
        const test = keywords[i].map(keyword => {
          if (keyword.length === 1) {
            return (
              curr.date.includes(keyword) ||
              curr.place.includes(keyword) ||
              curr.person.includes(keyword) ||
              curr.subject.includes(keyword) ||
              curr.reason.includes(keyword) ||
              curr.condition.includes(keyword) ||
              curr.data.includes(keyword) ||
              curr.unit.includes(keyword)
            );
          } else {
            return (
              curr.date.toLowerCase().includes(keyword) ||
              curr.place.toLowerCase().includes(keyword) ||
              curr.person.toLowerCase().includes(keyword) ||
              curr.subject.toLowerCase().includes(keyword) ||
              curr.reason.toLowerCase().includes(keyword) ||
              curr.condition.toLowerCase().includes(keyword) ||
              curr.data.toLowerCase().includes(keyword) ||
              curr.unit.toLowerCase().includes(keyword)
            );
          }
        });
        return test.every(logic => logic === true);
      });
      foundKirks = foundKirks.concat(foundKirks_temp);
    }

    var foundKirks2 = [];
    for (let i = 0; i < exwords.length; i++) {
      const foundKirks2_temp = foundKirks.filter(curr => {
        const test = exwords[i].map(exword => {
          if (exword.length === 1) {
            return (
              curr.date.includes(exword) ||
              curr.place.includes(exword) ||
              curr.person.includes(exword) ||
              curr.subject.includes(exword) ||
              curr.reason.includes(exword) ||
              curr.condition.includes(exword) ||
              curr.data.includes(exword) ||
              curr.unit.includes(exword)
            );
          } else {
            return (
              curr.date.toLowerCase().includes(exword) ||
              curr.place.toLowerCase().includes(exword) ||
              curr.person.toLowerCase().includes(exword) ||
              curr.subject.toLowerCase().includes(exword) ||
              curr.reason.toLowerCase().includes(exword) ||
              curr.condition.toLowerCase().includes(exword) ||
              curr.data.toLowerCase().includes(exword) ||
              curr.unit.toLowerCase().includes(exword)
            );
          }
        });
        return test.every(logic => logic === false);
      });
      foundKirks2 = foundKirks2.concat(foundKirks2_temp);
    }

    // Apply sorting based on ListData field buttons
    foundKirks2 = applySorting(foundKirks2);

    function onlyUnique (value, index, array) {
      return array.indexOf(value) === index;
    }
    foundKirks2 = foundKirks2.filter(onlyUnique);

    const csvData = foundKirks2;

    // Extract keywords for highlighting
    const searchKeywords = extractSearchKeywords(props.keyword);

    // Page number each item currently lands on (list is already in final display order)
    const pageById = {};
    foundKirks2.forEach((inp, idx) => {
      pageById[inp._id] = Math.floor(idx / kirkListPerPage) + 1;
    });

    // Deep linking: jump to wherever the linked item currently lives, computed
    // fresh from the live list above rather than a page number stored in the
    // URL (which would go stale as new records shift everything's position).
    const deepLinkItemId = searchParams.get('id');
    if (deepLinkItemId && pageById[deepLinkItemId] !== undefined) {
      pendingDeepLinkPageRef.current = { id: deepLinkItemId, page: pageById[deepLinkItemId] };
    }

    const kirkList = foundKirks2.map(kirk => {
      return (
        <div key={kirk._id}>
          <RenderKirkItem
            kirk={kirk}
            deleteKirk={props.deleteKirk}
            updateKirk={props.updateKirk}
            resetKirkForm={props.resetKirkForm}
            auth={props.auth}
            isUpdating={props.isUpdating}
            searchKeywords={searchKeywords}
            keyword={props.keyword}
            view={props.view}
            htmlResumeRevision={props.htmlResumeRevision}
          />
        </div>
      );
    });

    num = kirkList.length;

    const indexOfLastKirkList = currentPage * kirkListPerPage;
    const indexOfFirstKirkList = indexOfLastKirkList - kirkListPerPage;
    const currentKirkList = kirkList.slice(indexOfFirstKirkList, indexOfLastKirkList);

    return (
      <div>
        <Card>
          <Row className='form-group'>
            <Col>
              <CardBody>
                <div className='row'>
                  <div className='col-6'>
                    <h5>{num} items</h5>
                  </div>
                  <div className='col-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                    <CSVLink
                      data={csvData}
                      className='fa fa-arrow-down'
                    ></CSVLink>
                    <Button
                      outline
                      size='sm'
                      color={pinEnabled ? 'primary' : 'secondary'}
                      onClick={() => setPinEnabled(!pinEnabled)}
                      title={pinEnabled ? 'Pinning is on: pinned items float to the top. Click to turn off.' : 'Pinning is off: pinned items are ignored. Click to turn on.'}
                    >
                      <span className='fa fa-thumb-tack'></span>
                    </Button>
                  </div>
                </div>
                {currentKirkList}
              </CardBody>
            </Col>
          </Row>
        </Card>
        <Pagination
          inputListPerPage={kirkListPerPage}
          totalInputList={kirkList.length}
          paginate={paginate}
          currentPage={currentPage}
        />
        {/* Scroll toggle button */}
        <button
          style={scrollButtonStyle}
          onClick={toggleScrollPosition}
          aria-label={isAtTop && savedScrollPosition !== null ? 'Go back' : 'Scroll to top'}
        >
          <span className={isAtTop && savedScrollPosition !== null ? 'fa fa-arrow-down' : 'fa fa-arrow-up'}></span>
        </button>
      </div>
    );
  } else if (props.kirks !== null) {
    // Apply sorting based on ListData field buttons
    const sortedKirks = applySorting(props.kirks);

    // sortedKirks is already in final display order (newest-first, pinned-first)
    const pageById = {};
    sortedKirks.forEach((inp, idx) => {
      pageById[inp._id] = Math.floor(idx / kirkListPerPage) + 1;
    });

    // Deep linking: jump to wherever the linked item currently lives, computed
    // fresh from the live list above rather than a page number stored in the
    // URL (which would go stale as new records shift everything's position).
    const deepLinkItemId = searchParams.get('id');
    if (deepLinkItemId && pageById[deepLinkItemId] !== undefined) {
      pendingDeepLinkPageRef.current = { id: deepLinkItemId, page: pageById[deepLinkItemId] };
    }

    const kirkList = sortedKirks.map(kirk => {
      return (
        <div key={kirk._id}>
          <RenderKirkItem
            kirk={kirk}
            deleteKirk={props.deleteKirk}
            updateKirk={props.updateKirk}
            resetKirkForm={props.resetKirkForm}
            auth={props.auth}
            isUpdating={props.isUpdating}
            searchKeywords={[]}
            keyword={props.keyword}
            view={props.view}
            htmlResumeRevision={props.htmlResumeRevision}
          />
        </div>
      );
    });

    num = kirkList.length;

    const csvData = sortedKirks;

    const indexOfLastKirkList = currentPage * kirkListPerPage;
    const indexOfFirstKirkList = indexOfLastKirkList - kirkListPerPage;
    const currentKirkList = kirkList.slice(indexOfFirstKirkList, indexOfLastKirkList);

    return (
      <div>
        <Card>
          <Row className='form-group'>
            <Col>
              <CardBody>
                <div className='row'>
                  <div className='col-6'>
                    <h5>{num} items</h5>
                  </div>
                  <div className='col-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
                    <CSVLink
                      data={csvData}
                      className='fa fa-arrow-down'
                    ></CSVLink>
                    <Button
                      outline
                      size='sm'
                      color={pinEnabled ? 'primary' : 'secondary'}
                      onClick={() => setPinEnabled(!pinEnabled)}
                      title={pinEnabled ? 'Pinning is on: pinned items float to the top. Click to turn off.' : 'Pinning is off: pinned items are ignored. Click to turn on.'}
                    >
                      <span className='fa fa-thumb-tack'></span>
                    </Button>
                  </div>
                </div>
                {currentKirkList}
              </CardBody>
            </Col>
          </Row>
        </Card>
        <Pagination
          inputListPerPage={kirkListPerPage}
          totalInputList={kirkList.length}
          paginate={paginate}
          currentPage={currentPage}
        />
        {/* Scroll toggle button */}
        <button
          style={scrollButtonStyle}
          onClick={toggleScrollPosition}
          aria-label={isAtTop && savedScrollPosition !== null ? 'Go back' : 'Scroll to top'}
        >
          <span className={isAtTop && savedScrollPosition !== null ? 'fa fa-arrow-down' : 'fa fa-arrow-up'}></span>
        </button>
      </div>
    );
  } else {
    return <div></div>;
  }
}
